import React from 'react';
import { useReveal, useTilt } from '../shell';
import { apps } from '../data';

/**
 * One app.
 *
 * Split out from the map so each card can hold a tilt ref of its own — a hook
 * cannot be called inside a callback, and one shared ref would tilt only the
 * last card.
 */
const AppCard = ({ app }) => {
  const tilt = useTilt();

  return (
    <article ref={tilt} className="m-card m-reveal">
      <div className="m-card__media">
        <img
          src={app.image}
          alt={app.title}
          loading="lazy"
          decoding="async"
          width="640"
          height="400"
        />
      </div>

      <div className="m-card__body">
        <div className="m-card__kicker">
          {app.platform} · {app.type} · {app.size}
        </div>
        <h3 className="m-card__title">{app.title}</h3>
        <p className="m-card__short">{app.description}</p>

        <div className="m-tags">
          {app.features.map((f) => (
            <span key={f} className="m-tag">
              {f}
            </span>
          ))}
        </div>
      </div>

      {/* Same footer row as a project card, so the grid reads as one system. */}
      <div className="m-card__links">
        <a
          className="m-btn m-btn--primary"
          href={app.href}
          // A Web Store listing is a link out; everything else is a file the
          // browser should save.
          target={app.external ? '_blank' : undefined}
          rel={app.external ? 'noopener noreferrer' : undefined}
          download={app.external ? undefined : true}
        >
          {app.cta}
        </a>
        <span className="m-card__meta">v{app.version}</span>
      </div>
    </article>
  );
};

/**
 * My Apps.
 *
 * Same card grammar as Projects, but the action is a download or a store link
 * rather than a modal — so there is no detail view and the card is an article,
 * not a button.
 */
const Apps = () => {
  useReveal([]);

  return (
    <>
      <section className="m-head">
        <div className="m-head__kicker">My Apps</div>
        <h1 className="m-head__title">
          Built and
          <br />
          shipped
        </h1>
        <p className="m-head__lead">
          Installable software for Windows, Android and Chrome. Sizes are stated up front — one of
          these is 95 MB.
        </p>
      </section>

      <section className="m-section--grid">
        <div className="m-cards">
          {apps.map((app) => (
            <AppCard key={app.id} app={app} />
          ))}
        </div>
      </section>
    </>
  );
};

export default Apps;
